import { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  UserPlus, Trash2, Pencil, Building2, Users, Archive, Download, ShieldAlert, X,
  Plus, KeyRound, CalendarClock, HardDriveDownload, Database, RefreshCw, Crown, Save,
} from "lucide-react";

const ROLES = ["SPV", "Leader", "Staff"];
const SERVICE_OPTS = ["PPh 21", "PPh 23", "PPh Final", "Monthly Bookkeeping", "Annual SPT", "PPN / VAT"];
const FREQ = ["daily", "weekly", "monthly", "quarterly", "yearly"];
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const emptyClient = {
  company_name: "", category: "", logo: "", npwp: "", nib: "", address: "", pic_name: "", email: "", phone: "",
  accounts: [], confidential_notes: "", services: [], bpjs_setup: "", delegated_to: "", archived: false,
};

function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs font-semibold text-[var(--text-secondary)]">{label}</label>
      <input {...props} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white outline-none focus:border-[var(--gold)] focus:ring-2 focus:ring-[var(--gold)]/15 transition text-sm" />
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [archived, setArchived] = useState([]);

  const loadAll = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/clients?archived=false").then((r) => setClients(r.data));
    api.get("/clients?archived=true").then((r) => setArchived(r.data));
  };
  useEffect(loadAll, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] font-semibold">Feature 1</div>
        <h1 className="font-serif text-4xl mt-1">Settings</h1>
        <p className="text-[var(--text-secondary)] mt-1 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-[var(--gold)]" />
          {user.role === "SuperAdmin" ? "Super Admin — full master controls." : user.role === "SPV" ? "SPV access — add, edit & delete." : "Leader access — you can add new records only."}
        </p>
      </div>

      <div className="flex gap-2 border-b border-[var(--border-subtle)] flex-wrap">
        {[
          { id: "users", label: "User & Roles", icon: Users },
          { id: "clients", label: "Client Database", icon: Building2 },
          { id: "archive", label: "Archive", icon: Archive },
          ...((user.role === "SPV" || user.role === "SuperAdmin") ? [{ id: "data", label: "Data Management", icon: Database }] : []),
          ...(user.role === "SuperAdmin" ? [{ id: "master", label: "Master Controls", icon: Crown }] : []),
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`settings-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${tab === t.id ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersPanel users={users} reload={loadAll} />}
      {tab === "clients" && <ClientsPanel clients={clients} reload={loadAll} users={users} />}
      {tab === "archive" && <ArchivePanel archived={archived} reload={loadAll} />}
      {tab === "data" && <DataPanel />}
      {tab === "master" && <MasterPanel />}
    </div>
  );
}

function UsersPanel({ users, reload }) {
  const { can, user: me } = useAuth();
  const roleOptions = me?.role === "SuperAdmin" ? ["SuperAdmin", ...ROLES] : ROLES;
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ id_code: "", name: "", password: "", role: "Staff", email: "", position: "", phone: "" });

  const openNew = () => { setEdit(null); setForm({ id_code: "", name: "", password: "", role: "Staff", email: "", position: "", phone: "" }); setOpen(true); };
  const openEdit = (u) => { setEdit(u); setForm({ ...u, password: "" }); setOpen(true); };

  const save = async () => {
    try {
      if (edit) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${edit.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    toast.success("User deleted");
    reload();
  };

  const roleBadge = { SuperAdmin: "bg-[var(--gold)]/25 text-[var(--gold-hover)]", SPV: "bg-[var(--gold)]/15 text-[var(--gold-hover)]", Leader: "bg-teal-50 text-teal-700", Staff: "bg-gray-100 text-gray-600" };

  return (
    <div className="card-elegant p-6">
      <div className="flex justify-between items-center mb-5">
        <h3 className="font-serif text-xl">Users & Roles</h3>
        <button data-testid="add-user-button" onClick={openNew} className="btn-gold px-4 py-2 text-sm flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> Add User
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[var(--text-muted)] text-xs uppercase border-b border-[var(--border-subtle)]">
              <th className="py-3">ID Code</th><th>Name</th><th>Email</th><th>Role</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id_code}`} className="border-b border-[var(--border-subtle)] hover:bg-[var(--gold-light)]/40">
                <td className="py-3 font-mono font-medium">{u.id_code}</td>
                <td>{u.name}</td>
                <td className="text-[var(--text-secondary)] text-xs">{u.email || "—"}</td>
                <td><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${roleBadge[u.role]}`}>{u.role}</span></td>
                <td className="text-right">
                  {can("edit") && <button onClick={() => openEdit(u)} className="text-[var(--text-muted)] hover:text-[var(--gold)] p-1.5"><Pencil className="w-4 h-4" /></button>}
                  {can("delete") && <button data-testid={`delete-user-${u.id_code}`} onClick={() => del(u.id)} className="text-[var(--text-muted)] hover:text-[var(--urgent)] p-1.5"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal title={edit ? "Edit User" : "Add New User"} onClose={() => setOpen(false)}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="ID Code" data-testid="user-form-idcode" value={form.id_code} disabled={!!edit} onChange={(e) => setForm({ ...form, id_code: e.target.value.toUpperCase() })} />
            <Field label="Full Name" data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Field label="Position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
            <Field label="Email (for task alerts)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Field label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">Role</label>
              <select data-testid="user-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none focus:border-[var(--gold)]">
                {roleOptions.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <Field label={edit ? "New Password (blank = keep)" : "Password"} type="password" data-testid="user-form-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-subtle)]">Cancel</button>
            <button data-testid="user-form-save" onClick={save} className="btn-gold px-5 py-2 text-sm">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ClientsPanel({ clients, reload, users }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyClient);
  const [templates, setTemplates] = useState([]);
  const [newTpl, setNewTpl] = useState(null);

  const openNew = () => { setEdit(null); setForm(emptyClient); setTemplates([]); setStep(1); setOpen(true); };
  const openEdit = async (c) => {
    setEdit(c); setForm({ ...emptyClient, ...c, accounts: c.accounts || [] }); setStep(1); setOpen(true);
    const r = await api.get(`/task-templates?client_id=${c.id}`);
    setTemplates(r.data);
  };

  const toggleService = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const addAccount = () => setForm((f) => ({ ...f, accounts: [...(f.accounts || []), { name: "", username: "", password: "" }] }));
  const updAccount = (i, k, v) => setForm((f) => { const a = [...f.accounts]; a[i] = { ...a[i], [k]: v }; return { ...f, accounts: a }; });
  const rmAccount = (i) => setForm((f) => ({ ...f, accounts: f.accounts.filter((_, x) => x !== i) }));

  const save = async () => {
    try {
      let clientId = edit?.id;
      const payload = { ...form };
      if (edit) { await api.put(`/clients/${edit.id}`, payload); }
      else { const res = await api.post("/clients", payload); clientId = res.data.id; }
      // create any pending templates (no id yet)
      for (const t of templates.filter((t) => !t.id)) {
        await api.post("/task-templates", { ...t, client_id: clientId });
      }
      toast.success(edit ? "Client updated" : "Client added");
      setOpen(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete client?")) return; await api.delete(`/clients/${id}`); toast.success("Deleted"); reload(); };
  const archive = async (id) => { await api.post(`/clients/${id}/archive`); toast.success("Moved to archive"); reload(); };

  const addTemplateLocal = () => setNewTpl({ name: "", frequency: "monthly", weekday: 0, due_day: 20, due_month: 1, description: "", delegated_to: "", lead_days: "" });
  const saveTemplate = async () => {
    if (!newTpl.name) return toast.error("Task name required");
    if (edit?.id) {
      const res = await api.post("/task-templates", { ...newTpl, client_id: edit.id });
      setTemplates((t) => [...t, res.data]);
    } else {
      setTemplates((t) => [...t, { ...newTpl }]);
    }
    setNewTpl(null);
  };
  const delTemplate = async (t, i) => {
    if (t.id) await api.delete(`/task-templates/${t.id}`);
    setTemplates((arr) => arr.filter((_, x) => x !== i));
  };
  const extendTemplate = async (t) => {
    const r = await api.post(`/task-templates/${t.id}/extend`);
    setTemplates((arr) => arr.map((x) => (x.id === t.id ? { ...x, active_until: r.data.active_until } : x)));
    toast.success("Extended 1 year");
  };

  return (
    <div className="card-elegant p-6">
      <div className="flex justify-between items-center mb-5">
        <h3 className="font-serif text-xl">Client Onboarding Database</h3>
        <button data-testid="settings-add-client-button" onClick={openNew} className="btn-gold px-4 py-2 text-sm flex items-center gap-2">
          <Building2 className="w-4 h-4" /> Add Client
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[var(--text-muted)] text-xs uppercase border-b border-[var(--border-subtle)]">
              <th className="py-3">Company</th><th>Category</th><th>NPWP</th><th>NIB</th><th>Accounts</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--gold-light)]/40">
                <td className="py-3 font-medium">{c.company_name}</td>
                <td className="text-[var(--text-secondary)]">{c.category}</td>
                <td className="font-mono text-xs">{c.npwp || "—"}</td>
                <td className="font-mono text-xs">{c.nib || "—"}</td>
                <td className="text-xs">{(c.accounts || []).length} account(s)</td>
                <td className="text-right whitespace-nowrap">
                  {can("edit") && <button onClick={() => openEdit(c)} className="text-[var(--text-muted)] hover:text-[var(--gold)] p-1.5"><Pencil className="w-4 h-4" /></button>}
                  <button onClick={() => archive(c.id)} className="text-[var(--text-muted)] hover:text-[var(--teal)] p-1.5"><Archive className="w-4 h-4" /></button>
                  {can("delete") && <button onClick={() => del(c.id)} className="text-[var(--text-muted)] hover:text-[var(--urgent)] p-1.5"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
            {clients.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-[var(--text-muted)]">No clients yet. Add your first client.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal wide title={edit ? "Edit Client" : "Add New Client"} onClose={() => setOpen(false)}>
          <div className="flex gap-2 mb-5">
            {["Basic Information", "Password & Access", "Tax & Scheduled Tasks"].map((s, i) => (
              <button key={s} onClick={() => setStep(i + 1)} className={`flex-1 py-2 text-xs font-semibold rounded-lg border transition ${step === i + 1 ? "gold-bg text-white border-[var(--gold)]" : "border-[var(--border-subtle)] text-[var(--text-secondary)]"}`}>
                {i + 1}. {s}
              </button>
            ))}
          </div>

          {step === 1 && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Company Name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
              <Field label="Category / Industry" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <div className="col-span-2">
                <label className="text-xs font-semibold text-[var(--text-secondary)]">Company Logo (upload image)</label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl border border-[var(--border-gold)] bg-[var(--gold-light)] flex items-center justify-center overflow-hidden shrink-0">
                    {form.logo ? <img src={form.logo} alt="logo" className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 text-[var(--gold)]" />}
                  </div>
                  <input data-testid="client-logo-upload" type="file" accept="image/*" onChange={(e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    if (file.size > 1024 * 1024) { toast.error("Image must be under 1MB"); return; }
                    const reader = new FileReader(); reader.onload = () => setForm((f) => ({ ...f, logo: reader.result })); reader.readAsDataURL(file);
                  }} className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[var(--gold)] file:text-white file:font-medium file:cursor-pointer" />
                  {form.logo && <button type="button" onClick={() => setForm({ ...form, logo: "" })} className="text-xs text-[var(--urgent)] hover:underline">Remove</button>}
                </div>
              </div>
              <Field label="NPWP" value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} />
              <Field label="NIB Number" data-testid="client-nib-input" value={form.nib} onChange={(e) => setForm({ ...form, nib: e.target.value })} />
              <Field label="PIC Name" value={form.pic_name} onChange={(e) => setForm({ ...form, pic_name: e.target.value })} />
              <Field label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Field label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Field label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[var(--text-secondary)] text-sm"><KeyRound className="w-4 h-4 text-[var(--gold)]" /> Add unlimited access accounts (Name · Username · Password)</div>
                <button data-testid="add-account-button" onClick={addAccount} className="btn-gold px-3 py-1.5 text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add Account</button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-[10px] uppercase font-semibold text-[var(--text-muted)] px-1">
                  <span>Account Name</span><span>Username</span><span>Password</span><span></span>
                </div>
                {(form.accounts || []).map((a, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                    <input data-testid={`account-name-${i}`} value={a.name} onChange={(e) => updAccount(i, "name", e.target.value)} placeholder="e.g. DJP Online" className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" />
                    <input data-testid={`account-username-${i}`} value={a.username} onChange={(e) => updAccount(i, "username", e.target.value)} placeholder="username" className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm font-mono outline-none focus:border-[var(--gold)]" />
                    <input data-testid={`account-password-${i}`} value={a.password} onChange={(e) => updAccount(i, "password", e.target.value)} placeholder="password" className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm font-mono outline-none focus:border-[var(--gold)]" />
                    <button onClick={() => rmAccount(i)} className="text-[var(--text-muted)] hover:text-[var(--urgent)] p-2"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {(form.accounts || []).length === 0 && <div className="text-sm text-[var(--text-muted)] py-4 text-center">No accounts yet.</div>}
              </div>
              <div>
                <label className="text-xs font-semibold text-[var(--text-secondary)]">Confidential Notes</label>
                <textarea value={form.confidential_notes} onChange={(e) => setForm({ ...form, confidential_notes: e.target.value })} rows="2" className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <label className="text-xs font-semibold text-[var(--text-secondary)]">Tax Services</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SERVICE_OPTS.map((s) => (
                    <button key={s} onClick={() => toggleService(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${form.services.includes(s) ? "gold-bg text-white border-[var(--gold)]" : "border-[var(--border-subtle)] text-[var(--text-secondary)]"}`}>{s}</button>
                  ))}
                </div>
              </div>

              <div className="border-t border-[var(--border-subtle)] pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm font-semibold"><CalendarClock className="w-4 h-4 text-[var(--gold)]" /> Scheduled Tasks (auto-recur in Project Tracker)</div>
                  <button data-testid="add-template-button" onClick={addTemplateLocal} className="btn-gold px-3 py-1.5 text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add Task</button>
                </div>
                <div className="space-y-2">
                  {templates.map((t, i) => (
                    <div key={i} className="flex items-center justify-between bg-[var(--bg-subtle)] rounded-lg px-3 py-2 text-sm">
                      <div>
                        <span className="font-medium">{t.name}</span>
                        <span className="ml-2 text-xs px-2 py-0.5 rounded bg-white capitalize">{t.frequency}</span>
                        {t.active_until && <span className="ml-2 text-[10px] text-[var(--text-muted)]">until {t.active_until}</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        {t.id && <button onClick={() => extendTemplate(t)} title="Extend 1 year" className="text-[var(--teal)] p-1"><RefreshCw className="w-3.5 h-3.5" /></button>}
                        <button onClick={() => delTemplate(t, i)} className="text-[var(--text-muted)] hover:text-[var(--urgent)] p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                  {templates.length === 0 && !newTpl && <div className="text-sm text-[var(--text-muted)] py-2">No scheduled tasks. Add one to auto-populate the Project Tracker.</div>}
                </div>

                {newTpl && (
                  <div className="mt-3 border border-[var(--border-gold)] rounded-xl p-4 space-y-3 bg-[var(--gold-light)]/40">
                    <input data-testid="template-name-input" value={newTpl.name} onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })} placeholder="Task name (e.g. Submit Monthly PPh 21)" className="w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" />
                    <div className="grid grid-cols-3 gap-2">
                      <select data-testid="template-frequency" value={newTpl.frequency} onChange={(e) => setNewTpl({ ...newTpl, frequency: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm capitalize outline-none">
                        {FREQ.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                      {newTpl.frequency === "weekly" ? (
                        <select value={newTpl.weekday} onChange={(e) => setNewTpl({ ...newTpl, weekday: +e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none">
                          {WEEKDAYS.map((d, x) => <option key={d} value={x}>{d}</option>)}
                        </select>
                      ) : newTpl.frequency !== "daily" ? (
                        <input type="number" min="1" max="31" value={newTpl.due_day} onChange={(e) => setNewTpl({ ...newTpl, due_day: +e.target.value })} placeholder="Day" className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none" />
                      ) : <div />}
                      {newTpl.frequency === "yearly" && (
                        <input type="number" min="1" max="12" value={newTpl.due_month} onChange={(e) => setNewTpl({ ...newTpl, due_month: +e.target.value })} placeholder="Month" className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none" />
                      )}
                    </div>
                    <textarea value={newTpl.description} onChange={(e) => setNewTpl({ ...newTpl, description: e.target.value })} rows="2" placeholder="Description / detail of the tax task" className="w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" />
                    <div>
                      <label className="text-xs font-semibold text-[var(--text-secondary)]">Reminder lead time (days before due it turns Urgent) — leave blank for default</label>
                      <input data-testid="template-lead-days" type="number" min="0" max="60" value={newTpl.lead_days} onChange={(e) => setNewTpl({ ...newTpl, lead_days: e.target.value === "" ? "" : +e.target.value })} placeholder="e.g. 5" className="mt-1 w-40 px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" />
                    </div>
                    <select value={newTpl.delegated_to} onChange={(e) => setNewTpl({ ...newTpl, delegated_to: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none">
                      <option value="">— Delegate to (optional) —</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                    </select>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setNewTpl(null)} className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-subtle)]">Cancel</button>
                      <button data-testid="template-save-button" onClick={saveTemplate} className="btn-gold px-4 py-1.5 text-xs">Add Task</button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-[var(--text-secondary)]">Delegate Overall Tax To</label>
                <select value={form.delegated_to} onChange={(e) => setForm({ ...form, delegated_to: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none focus:border-[var(--gold)]">
                  <option value="">— Not delegated —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-subtle)] disabled:opacity-40">Back</button>
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} className="btn-gold px-5 py-2 text-sm">Next</button>
            ) : (
              <button data-testid="client-form-save" onClick={save} className="btn-gold px-5 py-2 text-sm">Save Client</button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function ArchivePanel({ archived, reload }) {
  return (
    <div className="card-elegant p-6">
      <div className="flex justify-between items-center mb-5">
        <h3 className="font-serif text-xl">Archived Clients</h3>
        <button data-testid="export-archive-button" onClick={() => downloadFile("/export/clients", "SV Clients.xlsx")} className="btn-gold px-4 py-2 text-sm flex items-center gap-2">
          <Download className="w-4 h-4" /> Export All (.xlsx)
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[var(--text-muted)] text-xs uppercase border-b border-[var(--border-subtle)]">
            <th className="py-3">Company</th><th>Category</th><th className="text-right">Restore</th>
          </tr>
        </thead>
        <tbody>
          {archived.map((c) => (
            <tr key={c.id} className="border-b border-[var(--border-subtle)]">
              <td className="py-3 font-medium">{c.company_name}</td>
              <td className="text-[var(--text-secondary)]">{c.category}</td>
              <td className="text-right">
                <button onClick={async () => { await api.post(`/clients/${c.id}/archive`); toast.success("Restored"); reload(); }} className="text-[var(--gold)] text-xs font-semibold hover:underline">Restore</button>
              </td>
            </tr>
          ))}
          {archived.length === 0 && <tr><td colSpan="3" className="py-8 text-center text-[var(--text-muted)]">Archive is empty.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function DataPanel() {
  const [busy, setBusy] = useState(false);
  const doDownload = async (path, name) => {
    setBusy(true);
    try { await downloadFile(path, name); toast.success("Download started"); }
    catch (e) { toast.error("Download failed"); }
    finally { setBusy(false); }
  };
  const cards = [
    { title: "Full Backup (.zip)", desc: "All modules — Clients, Users, Tasks, Attendance, Payroll — as Excel + JSON in folders.", path: "/backup", name: "SV Backup.zip", testid: "backup-download-button", icon: HardDriveDownload, primary: true },
    { title: "Clients (.xlsx)", desc: "Full client onboarding database.", path: "/export/clients", name: "SV Clients.xlsx", testid: "data-export-clients", icon: Building2 },
    { title: "Project Tracker (.xlsx)", desc: "All tasks and statuses.", path: "/export/tasks", name: "SV ProjectTracker.xlsx", testid: "data-export-tasks", icon: CalendarClock },
  ];
  return (
    <div className="space-y-5">
      <div className="card-elegant p-6">
        <div className="flex items-center gap-2 mb-2"><Database className="w-5 h-5 text-[var(--gold)]" /><h3 className="font-serif text-xl">Data Management & Backup</h3></div>
        <p className="text-sm text-[var(--text-secondary)]">
          Your data is stored securely in the SAVIOR server database (MongoDB) and syncs automatically across all users.
          Use the options below to save a copy to your local device. Backups download as a ZIP with a folder per module (Excel + JSON inside).
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.title} className={`card-elegant p-6 flex flex-col ${c.primary ? "border-[var(--gold)]" : ""}`}>
            <c.icon className={`w-7 h-7 mb-3 ${c.primary ? "text-[var(--gold)]" : "text-[var(--text-secondary)]"}`} />
            <div className="font-semibold">{c.title}</div>
            <div className="text-xs text-[var(--text-secondary)] mt-1 flex-1">{c.desc}</div>
            <button data-testid={c.testid} disabled={busy} onClick={() => doDownload(c.path, c.name)} className="btn-gold mt-4 py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              <Download className="w-4 h-4" /> Download
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MasterPanel() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-[var(--text-muted)]">Loading…</div>;
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/settings", s); setS(r.data); toast.success("Master settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const Toggle = ({ k, label, desc }) => (
    <label className="flex items-start gap-3 p-4 rounded-xl border border-[var(--border-subtle)] cursor-pointer">
      <input data-testid={`master-${k}`} type="checkbox" checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} className="accent-[var(--gold)] w-4 h-4 mt-0.5" />
      <div><div className="text-sm font-semibold">{label}</div><div className="text-xs text-[var(--text-secondary)]">{desc}</div></div>
    </label>
  );
  return (
    <div className="card-elegant p-6 space-y-5">
      <div className="flex items-center gap-2"><Crown className="w-5 h-5 text-[var(--gold)]" /><h3 className="font-serif text-xl">Master Controls</h3><span className="text-xs text-[var(--text-muted)]">Super Admin only</span></div>

      <div>
        <label className="text-xs font-semibold text-[var(--text-secondary)]">Default Reminder Lead Time (days before due date a recurring task turns Urgent)</label>
        <input data-testid="master-reminder-lead-days" type="number" min="0" max="60" value={s.reminder_lead_days_default} onChange={(e) => setS({ ...s, reminder_lead_days_default: +e.target.value })} className="mt-1 w-40 px-3 py-2 rounded-lg border border-[var(--border-subtle)] font-mono text-sm outline-none focus:border-[var(--gold)]" />
        <div className="text-xs text-[var(--text-muted)] mt-1">Individual tasks can override this in their own settings.</div>
      </div>

      <Toggle k="daily_digest_enabled" label="Daily Digest Email" desc="Every morning, email each staff member a summary of their urgent & pending tasks." />
      <Toggle k="weekly_backup_enabled" label="Weekly Auto-Backup" desc="Every Monday, email a full backup ZIP to the address below." />

      <div>
        <label className="text-xs font-semibold text-[var(--text-secondary)]">Backup Recipient Email</label>
        <input data-testid="master-backup-email" value={s.backup_email || ""} onChange={(e) => setS({ ...s, backup_email: e.target.value })} className="mt-1 w-full max-w-md px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" />
      </div>

      <button data-testid="master-save-button" onClick={save} disabled={busy} className="btn-gold px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-60"><Save className="w-4 h-4" /> Save Master Settings</button>
    </div>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(17,19,24,0.5)" }}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-3xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto fade-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)] sticky top-0 bg-white z-10">
          <h3 className="font-serif text-xl">{title}</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--urgent)]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export { Modal };
